// axios 撤去 (issue #1672) に伴う共通 HTTP ヘルパー。
//
// fetch は 4xx/5xx でも reject しない。呼び出し元ごとに res.ok を書くと書き忘れが
// 混入し、エラーレスポンスを成功として扱ってしまう（送信失敗なのに完了表示になる等）。
// axios が担っていた「エラーステータスで例外を投げる」「undefined のパラメータを送らない」
// 「JSON をパースする」の3点をここに集約する。

export type QueryParams = Record<string, string | number | boolean | undefined | null>

type RequestOptions = {
  params?: QueryParams
  headers?: Record<string, string>
}

export class HttpError extends Error {
  readonly status: number
  readonly body: unknown

  constructor(status: number, body: unknown) {
    super(`HTTP ${status}`)
    this.name = 'HttpError'
    this.status = status
    this.body = body
  }

  // サーバが返す { error: "..." } を取り出す（無い場合は undefined）
  get serverErrorMessage(): string | undefined {
    const message = (this.body as { error?: unknown } | undefined)?.error
    return typeof message === 'string' ? message : undefined
  }
}

// axios の params 相当。undefined / null のキーは送らない。
// URLSearchParams に直接オブジェクトを渡すと、値が undefined のキーが
// 文字列 "undefined" として送られてしまうため、ここで落とす。
export const buildQuery = (params?: QueryParams): string => {
  if (!params) return ''

  const search = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return
    search.append(key, String(value))
  })
  return search.toString()
}

const withQuery = (url: string, params?: QueryParams): string => {
  const query = buildQuery(params)
  return query ? `${url}?${query}` : url
}

const throwUnlessOk = async (response: Response): Promise<void> => {
  if (response.ok) return

  // エラー応答が JSON とは限らない（Rails のエラーページ等）ため、パース失敗は握りつぶす
  let body: unknown
  try {
    body = await response.json()
  } catch {
    body = undefined
  }
  throw new HttpError(response.status, body)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getJson = async <T = any>(url: string, options: RequestOptions = {}): Promise<T> => {
  const response = await fetch(withQuery(url, options.params), {
    headers: { Accept: 'application/json', ...options.headers },
  })
  await throwUnlessOk(response)
  return response.json() as Promise<T>
}

// HTML パーシャルを返すエンドポイント用（例: LP ヘッダー価格）
export const getText = async (url: string, options: RequestOptions = {}): Promise<string> => {
  const response = await fetch(withQuery(url, options.params), { headers: { ...options.headers } })
  await throwUnlessOk(response)
  return response.text()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const postJson = async <T = any>(url: string, body: unknown, options: RequestOptions = {}): Promise<T> => {
  const response = await fetch(withQuery(url, options.params), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...options.headers },
    body: JSON.stringify(body),
  })
  await throwUnlessOk(response)
  return response.json() as Promise<T>
}
